import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { PageHeader, StatusBadge } from "../components/common";
import {
  Calendar as CalendarIcon, Clock, PenLine, Gavel, CheckSquare,
  Handshake, AlertTriangle, Loader2, ChevronLeft, ChevronRight, Filter,
} from "lucide-react";

const KIND_META = {
  obligation: { label: "Obligation", color: "#C5A059", icon: Clock },
  hearing: { label: "Hearing", color: "#2A0F5A", icon: Gavel },
  task: { label: "Task", color: "#4A5568", icon: CheckSquare },
  agreement: { label: "Agreement", color: "#215a3b", icon: Handshake },
  signature: { label: "Signature", color: "#7B56C9", icon: PenLine },
};

function ymd(d) { return d.toISOString().slice(0, 10); }
function monthRange(year, month) {
  const first = new Date(Date.UTC(year, month, 1));
  const last = new Date(Date.UTC(year, month + 1, 0));
  return { first, last, from: ymd(first), to: ymd(last) };
}

export default function CalendarPage() {
  const today = new Date();
  const [year, setYear] = useState(today.getUTCFullYear());
  const [month, setMonth] = useState(today.getUTCMonth());
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [kinds, setKinds] = useState(() => new Set(Object.keys(KIND_META)));
  const [view, setView] = useState("month"); // month | agenda

  const { first, last, from, to } = useMemo(() => monthRange(year, month), [year, month]);

  useEffect(() => {
    setLoading(true);
    api.get(`/calendar?from=${from}&to=${to}`)
      .then((r) => setEvents(r.data?.events || []))
      .finally(() => setLoading(false));
  }, [from, to]);

  const visible = useMemo(() =>
    events.filter((e) => kinds.has(e.kind)),
  [events, kinds]);

  const byDay = useMemo(() => {
    const m = {};
    for (const e of visible) {
      m[e.date] = m[e.date] || [];
      m[e.date].push(e);
    }
    return m;
  }, [visible]);

  const prev = () => {
    const n = new Date(Date.UTC(year, month - 1, 1));
    setYear(n.getUTCFullYear()); setMonth(n.getUTCMonth());
  };
  const next = () => {
    const n = new Date(Date.UTC(year, month + 1, 1));
    setYear(n.getUTCFullYear()); setMonth(n.getUTCMonth());
  };
  const jumpToday = () => {
    setYear(today.getUTCFullYear()); setMonth(today.getUTCMonth());
  };

  const monthLabel = new Date(Date.UTC(year, month, 1)).toLocaleString(undefined, {
    month: "long", year: "numeric", timeZone: "UTC",
  });

  const toggleKind = (k) => {
    setKinds((prev) => {
      const n = new Set(prev);
      n.has(k) ? n.delete(k) : n.add(k);
      return n;
    });
  };

  return (
    <div data-testid="calendar-page">
      <PageHeader
        eyebrow="Le Calendrier"
        title="Calendar & Deadlines"
        description="Every obligation, hearing, task, agreement renewal and signing deadline in a single, calm view."
      />

      {/* Controls */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div className="flex items-center gap-3">
          <button onClick={prev} className="w-8 h-8 border border-[#E8E3F0] bg-white flex items-center justify-center hover:border-[#2A0F5A]" data-testid="cal-prev"><ChevronLeft size={14} /></button>
          <div className="font-serif text-2xl min-w-[180px] text-center">{monthLabel}</div>
          <button onClick={next} className="w-8 h-8 border border-[#E8E3F0] bg-white flex items-center justify-center hover:border-[#2A0F5A]" data-testid="cal-next"><ChevronRight size={14} /></button>
          <button onClick={jumpToday} className="label-eyebrow px-3 py-1.5 border border-[#E8E3F0] hover:border-[#2A0F5A]" data-testid="cal-today">Today</button>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={() => setView("month")} className={`px-3 py-1.5 text-[10px] tracking-[0.18em] uppercase border ${view === "month" ? "bg-[#2A0F5A] text-[#FAFAF8] border-[#2A0F5A]" : "bg-white border-[#E8E3F0]"}`} data-testid="cal-view-month">Month</button>
          <button onClick={() => setView("agenda")} className={`px-3 py-1.5 text-[10px] tracking-[0.18em] uppercase border ${view === "agenda" ? "bg-[#2A0F5A] text-[#FAFAF8] border-[#2A0F5A]" : "bg-white border-[#E8E3F0]"}`} data-testid="cal-view-agenda">Agenda</button>
        </div>
      </div>

      {/* Kind filters */}
      <div className="flex flex-wrap items-center gap-2 mb-5">
        <span className="label-eyebrow flex items-center gap-1"><Filter size={11} /> Show</span>
        {Object.entries(KIND_META).map(([k, m]) => (
          <button
            key={k}
            onClick={() => toggleKind(k)}
            className={`px-2.5 py-1 text-[10px] tracking-[0.18em] uppercase border inline-flex items-center gap-1.5 ${kinds.has(k) ? "bg-white border-[#2A0F5A]" : "bg-[#FAFAF8] border-[#E8E3F0] opacity-40"}`}
            data-testid={`cal-kind-${k}`}
          >
            <span className="w-1.5 h-1.5" style={{ background: m.color }} />
            {m.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-sm text-[#4A5568] flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading…</div>
      ) : view === "month" ? (
        <MonthView year={year} month={month} byDay={byDay} today={today} />
      ) : (
        <AgendaView events={visible} />
      )}
    </div>
  );
}

function MonthView({ year, month, byDay, today }) {
  const first = new Date(Date.UTC(year, month, 1));
  const daysInMonth = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  const leading = (first.getUTCDay() + 6) % 7; // Monday first

  const cells = [];
  for (let i = 0; i < leading; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  const todayISO = ymd(today);

  return (
    <div className="border border-[#E8E3F0] bg-white" data-testid="cal-month-view">
      <div className="grid grid-cols-7 border-b border-[#E8E3F0]">
        {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => (
          <div key={d} className="label-eyebrow px-3 py-2 text-center">{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7">
        {cells.map((d, i) => {
          const date = d ? new Date(Date.UTC(year, month, d)).toISOString().slice(0, 10) : null;
          const items = (date && byDay[date]) || [];
          const isToday = date === todayISO;
          return (
            <div
              key={i}
              className={`min-h-[110px] border-r border-b border-[#E8E3F0] last-of-type:border-r-0 p-2 ${d ? "" : "bg-[#FAFAF8]"} ${isToday ? "bg-[#F6F2FB]" : ""}`}
              data-testid={d ? `cal-day-${date}` : undefined}
            >
              {d && (
                <>
                  <div className={`text-xs mb-1 ${isToday ? "font-semibold text-[#2A0F5A]" : "text-[#4A5568]"}`}>{d}</div>
                  <div className="space-y-1">
                    {items.slice(0, 3).map((e) => <EventPill key={e.id} e={e} />)}
                    {items.length > 3 && <div className="text-[10px] text-[#4A5568] pl-1">+{items.length - 3} more</div>}
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function AgendaView({ events }) {
  if (events.length === 0) {
    return (
      <div className="border border-[#E8E3F0] bg-white p-10 text-center text-sm text-[#4A5568]">
        No events in this window. Add a hearing or an obligation and it will appear here automatically.
      </div>
    );
  }
  // Group by date
  const groups = {};
  for (const e of events) { groups[e.date] = groups[e.date] || []; groups[e.date].push(e); }
  const dates = Object.keys(groups).sort();

  return (
    <div className="space-y-5" data-testid="cal-agenda-view">
      {dates.map((date) => (
        <div key={date}>
          <div className="label-eyebrow mb-2 text-[#C5A059]">{new Date(date + "T00:00:00Z").toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long", year: "numeric", timeZone: "UTC" })}</div>
          <div className="border border-[#E8E3F0] bg-white divide-y divide-[#E8E3F0]">
            {groups[date].map((e) => (
              <Link key={e.id} to={e.href || "#"} className="flex items-start gap-4 px-5 py-3 hover:bg-[#FAFAF8]" data-testid={`cal-event-${e.id}`}>
                <KindDot kind={e.kind} />
                <div className="flex-1 min-w-0">
                  <div className="font-serif text-base leading-tight">{e.title}</div>
                  {e.subtitle && <div className="text-xs text-[#4A5568] mt-0.5">{e.subtitle}</div>}
                </div>
                {e.status && <StatusBadge tone="neutral">{e.status}</StatusBadge>}
              </Link>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function EventPill({ e }) {
  const meta = KIND_META[e.kind] || { color: "#4A5568" };
  return (
    <Link
      to={e.href || "#"}
      className="block text-[10px] truncate px-1.5 py-0.5 border-l-2 bg-white hover:bg-[#FAFAF8]"
      style={{ borderLeftColor: meta.color }}
      title={e.title}
    >
      {e.title}
    </Link>
  );
}

function KindDot({ kind }) {
  const meta = KIND_META[kind] || { color: "#4A5568", icon: Clock, label: kind };
  const Icon = meta.icon;
  return (
    <div className="w-8 h-8 border border-[#E8E3F0] flex items-center justify-center shrink-0" style={{ color: meta.color }}>
      <Icon size={14} />
    </div>
  );
}
